/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

const pitchMultipliers: Record<string, number> = {
  "Flat (0/12)": 1.0,
  "Low (2/12)": 1.014,
  "3/12": 1.031,
  "4/12": 1.054,
  "5/12": 1.083,
  "6/12": 1.118,
  "7/12": 1.158,
  "8/12": 1.202,
  "9/12": 1.25,
  "10/12": 1.302,
  "11/12": 1.357,
  "12/12 (45°)": 1.414,
  "14/12": 1.537,
  "16/12": 1.667,
};

const roofTypes: Record<string, number> = {
  "Simple Gable": 0.10,
  "Hip Roof": 0.15,
  "Cross Gable": 0.13,
  "Complex (dormers, valleys)": 0.18,
};

export function RoofingCalculatorClient() {
  const [mode, setMode] = useState<"footprint" | "area">("footprint");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [pitch, setPitch] = useState("6/12");
  const [totalArea, setTotalArea] = useState("");
  const [roofType, setRoofType] = useState("Simple Gable");
  const [ridgeLength, setRidgeLength] = useState("");
  const [pricePerSquare, setPricePerSquare] = useState("");

  let roofArea = 0;
  if (mode === "footprint") {
    const l = parseFloat(length) || 0;
    const w = parseFloat(width) || 0;
    const multiplier = pitchMultipliers[pitch] || 1.0;
    roofArea = l * w * multiplier;
  } else {
    const ta = totalArea;
    roofArea = parseFloat(ta) || 0;
  }

  const wastePercent = roofTypes[roofType] || 0.10;
  const roofAreaWithWaste = roofArea * (1 + wastePercent);
  const squares = roofAreaWithWaste / 100; // 1 square = 100 sq ft
  const squaresRounded = Math.ceil(squares);
  const bundles = squaresRounded * 3; // 3 bundles per square (standard 3-tab or architectural)

  // Underlayment: 1 roll = 400 sq ft (standard #15 felt) or ~1000 sq ft (synthetic)
  const feltRolls = Math.ceil(roofAreaWithWaste / 400);
  const syntheticRolls = Math.ceil(roofAreaWithWaste / 1000);

  // Ridge cap: ~35 linear ft per bundle
  const ridge = parseFloat(ridgeLength) || 0;
  const ridgeCapBundles = ridge > 0 ? Math.ceil(ridge / 35) : 0;

  // Drip edge: perimeter / 10 (10 ft pieces)
  const l = parseFloat(length) || 0;
  const w = parseFloat(width) || 0;
  const perimeter = 2 * (l + w);
  const dripEdgePieces = perimeter > 0 ? Math.ceil(perimeter / 10) : 0;

  // Nails: ~320 per square (based on 4 nails per shingle)
  const nailPounds = Math.ceil((squaresRounded * 320) / 200); // ~200 nails per lb for roofing nails

  const ppsq = pricePerSquare;
  const price = parseFloat(ppsq) || 0;

  const hasResult = roofArea > 0;

  const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const fmtDec = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const inputClass = "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary";

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setMode("footprint")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${mode === "footprint" ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
        >
          Building Footprint
        </button>
        <button
          onClick={() => setMode("area")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${mode === "area" ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
        >
          Known Roof Area
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {mode === "footprint" ? (
          <>
            <div>
              <label className="block text-sm font-medium text-foreground">Building Length (ft)</label>
              <input type="number" value={length} onChange={(e) => setLength(e.target.value)} placeholder="50" className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Building Width (ft)</label>
              <input type="number" value={width} onChange={(e) => setWidth(e.target.value)} placeholder="30" className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Roof Pitch</label>
              <select value={pitch} onChange={(e) => setPitch(e.target.value)} className={inputClass}>
                {Object.entries(pitchMultipliers).map(([name, mult]) => (
                  <option key={name} value={name}>{name} (x{mult.toFixed(3)})</option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <div className="sm:col-span-3">
            <label className="block text-sm font-medium text-foreground">Total Roof Area (sq ft)</label>
            <input type="number" value={totalArea} onChange={(e) => setTotalArea(e.target.value)} placeholder="2000" className={inputClass} />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-foreground">Roof Type</label>
          <select value={roofType} onChange={(e) => setRoofType(e.target.value)} className={inputClass}>
            {Object.entries(roofTypes).map(([name, waste]) => (
              <option key={name} value={name}>{name} ({(waste * 100).toFixed(0)}% waste)</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Ridge Length (ft, optional)</label>
          <input type="number" value={ridgeLength} onChange={(e) => setRidgeLength(e.target.value)} placeholder="50" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Price per Square (optional)</label>
          <input type="number" value={pricePerSquare} onChange={(e) => setPricePerSquare(e.target.value)} placeholder="90" className={inputClass} />
        </div>
      </div>

      {hasResult && (
        <>
          <div className="mt-6 grid gap-4 rounded-lg bg-muted/50 p-4 sm:grid-cols-4">
            <div>
              <div className="text-sm text-muted-foreground">Roof Area</div>
              <div className="text-2xl font-bold text-foreground">{fmt(Math.round(roofArea))} sq ft</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Squares Needed</div>
              <div className="text-2xl font-bold text-secondary">{squaresRounded}</div>
              <div className="text-xs text-muted-foreground">Exact: {fmtDec(squares)}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Shingle Bundles</div>
              <div className="text-2xl font-bold text-foreground">{bundles}</div>
              <div className="text-xs text-muted-foreground">3 bundles/square</div>
            </div>
            {price > 0 && (
              <div>
                <div className="text-sm text-muted-foreground">Shingle Cost</div>
                <div className="text-2xl font-bold text-foreground">${fmt(squaresRounded * price)}</div>
              </div>
            )}
          </div>

          <div className="mt-4 rounded-lg border border-border p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Full Materials List</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shingle Bundles</span>
                <span className="font-medium text-foreground">{bundles}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Felt Underlayment (#15)</span>
                <span className="font-medium text-foreground">{feltRolls} roll{feltRolls > 1 ? "s" : ""}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Synthetic Underlayment (alt)</span>
                <span className="font-medium text-foreground">{syntheticRolls} roll{syntheticRolls > 1 ? "s" : ""}</span>
              </div>
              {ridgeCapBundles > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Ridge Cap Bundles</span>
                  <span className="font-medium text-foreground">{ridgeCapBundles}</span>
                </div>
              )}
              {dripEdgePieces > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Drip Edge (10&apos; pieces)</span>
                  <span className="font-medium text-foreground">{dripEdgePieces}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Roofing Nails</span>
                <span className="font-medium text-foreground">~{nailPounds} lbs</span>
              </div>
            </div>
          </div>
          <ResultActions
            toolSlug="roofing-calculator"
            inputs={{ mode, length, width, pitch, totalArea, roofType, ridgeLength, pricePerSquare }}
            outputs={{ roofArea, squaresRounded, bundles, feltRolls, syntheticRolls, ridgeCapBundles, dripEdgePieces, nailPounds }}
          />
        </>
      )}
    </div>
  );
}
